import React, { useCallback, useRef, useState } from 'react'
import { contentGet } from '../email-activity/content-api'
import { useLoad } from '../hooks/load'
import { Pill } from '../sso-workspace/pill'

// Groups an organization's identity provider manages (SCIM Groups, owner
// approval 2026-10-26): a group a connection's SCIM token made is the
// identity provider's. Its name and members are read-only here (content
// refuses a change: 409 group.idp_managed) while course access can still be
// granted to it, and it can still be deleted in an emergency (the identity
// provider makes it again, without its course access, the next time it
// provisions it). Content's GET /api/ddd/managed-groups lists the caller's
// organization's: [{ group_id, connection_id, connection_name }].

export const listManagedGroups = () => contentGet('managed-groups')

export const MANAGED_GROUP_LABEL = 'Managed by your identity provider'

export const managedGroupTip = connectionName =>
  `Your identity provider${connectionName ? ` (${connectionName})` : ''} adds this group and its members through provisioning. Change its name and members there.`

// The note on a managed group's page.
export const managedGroupNote = connectionName =>
  `${managedGroupTip(connectionName)} You can still give this group access to courses here.`

// What deleting a managed group does, said before it is deleted.
export const MANAGED_GROUP_DELETE_NOTE =
  'Your identity provider manages this group. If you delete it here, it is moved to the recycle bin, its course access stops, and your identity provider creates it again, empty of course access, the next time it provisions it. To remove it for good, remove it from provisioning in your identity provider.'

// What a picker leaves out, said where it matters.
export const MANAGED_GROUPS_LEFT_OUT = "Groups your identity provider manages aren't listed: it adds their members."

export const ManagedGroupChip = ({ connectionName }) =>
  <Pill tone="info" title={managedGroupTip(connectionName)}>{MANAGED_GROUP_LABEL}</Pill>

// toMap reads the route's rows into group id -> connection name.
export const toMap = rows => {
  const map = new Map()
  for (const row of Array.isArray(rows) ? rows : []) {
    const id = Number(row && row.group_id)
    if (Number.isFinite(id) && id > 0) map.set(id, (row && row.connection_name) || '')
  }
  return map
}

// The caller's organization's managed groups: managed(id) is the managing
// connection's name ('' when it has none) or null for a group the
// organization manages itself. ready is false until the list is read (or
// its read failed): pickers offer nothing before it. When it can't be read
// (no GROUP read, a database before SCIM Groups), every group reads as the
// organization's own: content still refuses a change to a managed one.
// Content counts a group as managed only while its link's SCIM token is
// live. enabled false reads nothing.
export const useManagedGroups = ({ enabled = true } = {}) => {
  const [map, setMap] = useState(() => new Map())
  const [ready, setReady] = useState(false)
  const latest = useRef(0)

  const reload = useCallback(async () => {
    if (!enabled) return
    const call = ++latest.current
    try {
      const rows = await listManagedGroups()
      if (call !== latest.current) return
      setMap(toMap(rows))
    } catch (err) {
      if (call !== latest.current) return
      setMap(new Map())
    } finally {
      if (call === latest.current) setReady(true)
    }
  }, [enabled])

  useLoad(() => reload(), [reload])

  const managed = useCallback(id => {
    const key = Number(id)
    return map.has(key) ? map.get(key) : null
  }, [map])

  return { managed, ready, reload }
}

// withoutManaged is rows without the managed groups (idOf reads a row's
// group id).
export const withoutManaged = (rows, managed, idOf = row => row && row.id) =>
  (Array.isArray(rows) ? rows : []).filter(row => managed(idOf(row)) === null)
